import { base64 } from '@seeneco-ui/core/utils/crypto';

import {
	createTypes,
	createAsyncAction,
} from '../../../flux';
import {
	MODULE_BANK_BNK_CMS,
	SBERBANK_BNK_CMS,
	OPEN_BANK_BNK_CMS,
	URALSIB_BNK_CMS,
} from '../../../shared/constants/cms';
import { getAuthParams } from '../../auth/utils';
import { OAUTH2_AUTHORIZATION_RESOURCE } from '../../../shared/constants/specific';
import { tenantSelectorsPack } from '../../tenant/selectors';


export type AuthorizeExternalSystemAccountOptions = {
	account: ExternalSystemAccount;
	ssi: SubsystemInstance;
	postActionURL?: string;
};

type ActionTypes = {
	'FETCH_CASH_MANAGEMENT_SYSTEMS': string;
	'FETCH_EXTERNAL_SYSTEM_ACCOUNTS': string;
	'FETCH_TENANT_INTEGRATIONS_DATA': string;
	'ADD_EXTERNAL_SYSTEM_ACCOUNT': string;
	'CHANGE_EXTERNAL_SYSTEM_ACCOUNT': string;
	'REMOVE_EXTERNAL_SYSTEM_ACCOUNT': string;
	'AUTHORIZE_EXTERNAL_SYSTEM_ACCOUNT': string;
};

const types = createTypes<ActionTypes>([
	'FETCH_CASH_MANAGEMENT_SYSTEMS',
	'FETCH_EXTERNAL_SYSTEM_ACCOUNTS',
	'FETCH_TENANT_INTEGRATIONS_DATA',
	'ADD_EXTERNAL_SYSTEM_ACCOUNT',
	'CHANGE_EXTERNAL_SYSTEM_ACCOUNT',
	'REMOVE_EXTERNAL_SYSTEM_ACCOUNT',
	'AUTHORIZE_EXTERNAL_SYSTEM_ACCOUNT',
], 'BUSINESS:INTEGRATIONS');

const actions = {
	fetchCashManagementSystems: createAsyncAction<Array<CashManagementSystem>>(types.FETCH_CASH_MANAGEMENT_SYSTEMS)(
		box => box.api.finance.funds.cashManagementSystemService.fetchCashManagementSystems(),
	),
	fetchExternalSystemAccounts: createAsyncAction<Array<ExternalSystemAccount>>(types.FETCH_EXTERNAL_SYSTEM_ACCOUNTS)(
		box => box.api.platform.security.externalSystemService.fetchExternalSystemAccounts(),
	),
	fetchTenantIntegrationsData: createAsyncAction<{
		ssInstanceList: Array<SubsystemInstance>,
		remoteExternalServiceMap: Record<string, RemoteExternalService>}>(types.FETCH_TENANT_INTEGRATIONS_DATA)(
		box => {
			return new Promise(resolve => {
				box.api.platform.security.externalSystemService.fetchExternalSubsystemInstances().then((ssInstanceList: Array<SubsystemInstance>) => {
					const promises = ssInstanceList.map(ssInstance => {
						return new Promise(resolve => {
							box.api.platform.configuration.subsystemService.fetchRemoteExternalServiceBySubsystemID(ssInstance.ID).then((result: RemoteExternalService) => {
								resolve({ [ssInstance.ID]: result });
							});
						});
					});
		
					Promise.all(promises).then((remoteExternalServiceList: Array<RemoteExternalService>) => {
						const remoteExternalServiceMap = remoteExternalServiceList.reduce((acc, el) => {
							acc[Object.keys(el)[0]] = el[Object.keys(el)[0]];
		
							return acc;
						}, {})
						resolve({ ssInstanceList, remoteExternalServiceMap });
					});
				});
			});
		}
	),
	addExternalSystemAccount: createAsyncAction<ExternalSystemAccount>(types.ADD_EXTERNAL_SYSTEM_ACCOUNT)(
		(box, account: ExternalSystemAccount) => box.api.platform.security.externalSystemService.addExternalSystemAccount(account),
	),
	changeExternalSystemAccount: createAsyncAction<ExternalSystemAccount>(types.CHANGE_EXTERNAL_SYSTEM_ACCOUNT)(
		(box, account: ExternalSystemAccount) => box.api.platform.security.externalSystemService.changeExternalSystemAccount(account),
	),
	removeExternalSystemAccount: createAsyncAction<boolean>(types.REMOVE_EXTERNAL_SYSTEM_ACCOUNT)(
		(box, ID: number) => box.api.platform.security.externalSystemService.removeExternalSystemAccount(ID),
	),
	authorizeExternalSystemAccount: createAsyncAction<boolean>(types.AUTHORIZE_EXTERNAL_SYSTEM_ACCOUNT)(
		(box, options: AuthorizeExternalSystemAccountOptions) => {
			const {
				account,
				ssi,
				postActionURL,
			} = options;
			const {
				openIDProfile,
				applicationScope,
				businessServiceID,
			} = getAuthParams();
			const ticketUID = tenantSelectorsPack.selectTicketUID(box.getState());

			return new Promise(resolve => {
				type RedirectParams = {
					redirectURLWithEncodedState?: string;
					redirectURLWithoutEncodedState?: string;
					ssiID?: number;
				};

				const makeRedirect = (guid: string, ssiID: number) => {
					const rootPath = location.origin;
					const encodedPostActionURL = encodeURIComponent(postActionURL || location.pathname);
					const redirectBaseURL = `${rootPath}/atr-framework-services/externalSystemOpenID`;
					const URLState = `start_url_context=${encodedPostActionURL}&ticketuid=${ticketUID}&acquire_token_mode=true&external_service_guid=${guid}&external_system_account_id=${account.ID}&business_service_id=${businessServiceID}`;
					const URLStateEncoded = base64.encode(`${URLState}&serialized_parameters=true`);
					const redirectURLWithEncodedState = `${redirectBaseURL}?request_auth_code=true&state=${URLStateEncoded}`;
					const redirectURLWithoutEncodedState = `${redirectBaseURL}?request_auth_code=true&${URLState}`;
					const params = {
						ssiID,
						redirectURLWithEncodedState,
						redirectURLWithoutEncodedState
					};
					const DEFAULT_REDIRECT_METHOD = 'DEFAULT_REDIRECT_METHOD';
					const map: Record<string, (p: RedirectParams) => () => void> = {
						[MODULE_BANK_BNK_CMS]: ({ redirectURLWithEncodedState, ssiID }) => () => {
							box.api.platform.configuration.subsystemService.fetchSubsystemInstanceByID(ssiID).then(ssInstance => {
								const resource = (ssInstance.ResourcesURL || []).find(r => r.ResourceTypeCode === OAUTH2_AUTHORIZATION_RESOURCE);
					
								if (resource) {
									createAndSubmitModuleBankForm(resource, redirectURLWithEncodedState, ssInstance.ExternalSystemApplicationUID);
								} else {
									console.log('Не найден путь авторизации к внешнему сервису');
								}
							});
						},
						[SBERBANK_BNK_CMS]: ({ redirectURLWithoutEncodedState }) => () => {
							const redirectURL = redirectURLWithoutEncodedState + `&open_id_profile=${openIDProfile}&application_scope=${applicationScope}`;
			
							location.replace(redirectURL);
						},
						[OPEN_BANK_BNK_CMS]: ({ redirectURLWithEncodedState }) => () => {
							location.replace(redirectURLWithEncodedState);
						},
						[URALSIB_BNK_CMS]: ({ redirectURLWithoutEncodedState }) => () => {
							const applicationScope = 'offline_access profile legal_info ApiResource_LK ApiResource_Bank'.replace(/\s/g, '+');
							const redirectURL = redirectURLWithoutEncodedState + `&application_scope=${applicationScope}`;
			
							window.location.replace(redirectURL);
						},
						[DEFAULT_REDIRECT_METHOD]: ({ redirectURLWithoutEncodedState }) => () => {
							location.replace(redirectURLWithoutEncodedState);
						}
					};
			
					return map[guid] ? map[guid](params) : map[DEFAULT_REDIRECT_METHOD](params);
				};

				const createAndSubmitModuleBankForm = (resource, redirectUri: string, ExternalSystemApplicationUID: string) => {
					const requestForm = document.createElement('form');
					requestForm.setAttribute('method', 'POST');
					requestForm.setAttribute('action', resource.URL);
				
					const clientIdField = document.createElement('input');
					clientIdField.setAttribute('type', 'hidden');
					clientIdField.setAttribute('name', 'clientId');
					clientIdField.setAttribute('value', ExternalSystemApplicationUID);
					requestForm.appendChild(clientIdField);
				
					const responseTypeField = document.createElement('input');
					responseTypeField.setAttribute('type', 'hidden');
					responseTypeField.setAttribute('name', 'responseType');
					responseTypeField.setAttribute('value', 'code');
					requestForm.appendChild(responseTypeField);
				
					const redirectUriField = document.createElement('input');
					redirectUriField.setAttribute('type', 'hidden');
					redirectUriField.setAttribute('name', 'redirectUri');
					redirectUriField.setAttribute('value', redirectUri);
					requestForm.appendChild(redirectUriField);
				
					const scopeField = document.createElement('input');
					scopeField.setAttribute('type', 'hidden');
					scopeField.setAttribute('name', 'scope');
					scopeField.setAttribute('value', 'account-info operation-history assistant-service');
					requestForm.appendChild(scopeField);
				
					document.body.appendChild(requestForm);
					requestForm.submit();
				};
			
				const redirect = makeRedirect(ssi.GUID, ssi.ID);
			
				redirect();
				resolve();
			});
		}
	),
};

export const integrationsActionsPack = {
	types,
	actions,
};